import {
  Card,
  CardBody,
  Avatar,
  Typography,
  Tabs,
  TabsHeader,
  Tab,
  Button,
  Input,
} from "@material-tailwind/react";
import {
  HomeIcon,
  Cog6ToothIcon,
  EyeIcon,
  EyeSlashIcon,
  DocumentPlusIcon,
  XCircleIcon,
  ArrowLeftIcon
} from "@heroicons/react/24/solid";
import Toaster from "../../components/Toaster";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from '@hookform/resolvers/yup';
import { updateProfile, changePassword, resetStatus, getUserProfile, clearUser } from "../../store/slices/userSlice";
import { useEffect, useRef, useState } from "react";
import ShowLoader from "@/components/Loader";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Select from 'react-select';
import { fetchCountryMobileCode, fetchCountry, fetchCity, fetchOffice, } from "../../store/slices/internalUsersSlice";
import FilePreview from "@/components/FilePreview";
import ScrollToTop from "@/components/ScrollToTop";
import DocumentViewModal from "@/components/DocumentViewModal";
import { deleteOtherDocument, getBusinessType } from "../../store/slices/customerSlice";
import {
  CountryOption,
  CountrySingleValue
} from "@/components/CountrySelectComponents";
import { useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";


const passwordSchema = (t) => yup.object({
  old_password: yup.string()
    .required(t("profile.old_password_required"))
    .min(6, t("profile.old_password_min"))
    .max(32, t("profile.old_password_max")),
  password: yup
    .string()
    .trim()
    .required(t("profile.new_password_required"))
    .matches(/\S/, t("profile.new_password_spaces"))
    .min(6, t("profile.new_password_min"))
    .max(50, t("profile.new_password_max"))
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]+$/,
      t("profile.password_weak")
    ),

  password_confirmation: yup
    .string()
    .required(t("validations.password_confirmation_required"))
    .oneOf([yup.ref("password"), null], t("validations.password_match")),
});


const noHTMLRegex = /^(?!.*<[^>]*>).*$/;
const noCodeRegex = /^(?!.*(javascript:|<script|<\/script>|<style|<\/style>|<iframe|<\/iframe>)).*$/i;
const notBlankRegex = /\S/;
const noConsecutiveSpecialChars = /^(?!.*([!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])\1).*$/;
const noLeadingTrailingPunct = /^(?!^[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])(?<![!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]$).*$/;


const fileValidation = (field, isImageRemoved, t) =>
  yup
    .mixed()
    .test("fileType", t("validations.invalid_file_type"), (value) => {
      if (!value || !(value instanceof File)) return true;

      const isImage = value.type.startsWith("image/");
      const isPdf = value.type === "application/pdf";

      // profile_image - ONLY image
      if (field === "profile_image") {
        return isImage;
      }

      // All other fields - allow image + PDF
      return isImage || isPdf;
    })
    .nullable()
    .when([], {
      is: () => isImageRemoved && isImageRemoved[field],
      then: (schema) =>
        schema.required(
          field === "residence_image"
            ? t("validations.id_proof_required")
            : t("validations.field_required", { field: field.replaceAll("_", " ").replace(/^./, (s) => s.toUpperCase()) })
        ),
      otherwise: (schema) => schema.notRequired(),
    });


const schema = (role = "", isImageRemoved = {}, t) =>
  yup.object({
    first_name: yup
      .string()
      .trim()
      .required(t("validations.first_name_required"))
      .matches(notBlankRegex, t("profile.first_name_spaces"))
      .matches(noHTMLRegex, t("validations.no_html_tags"))
      .matches(noCodeRegex, t("validations.no_code_content"))
      .matches(noConsecutiveSpecialChars, t("validations.no_consecutive_special_chars", { field: t("first_name") }))
      .matches(noLeadingTrailingPunct, t("validations.no_leading_trailing_punctuation", { field: t("first_name") }))
      .min(2, t("validations.first_name_min"))
      .max(30, t("validations.first_name_max")),

    last_name: yup
      .string()
      .trim()
      .nullable()
      .matches(notBlankRegex, t("profile.first_name_spaces"))
      .matches(noHTMLRegex, t("validations.no_html_tags"))
      .matches(noCodeRegex, t("validations.no_code_content"))
      .matches(noConsecutiveSpecialChars, t("profile.last_name_consecutive_special_chars"))
      .matches(noLeadingTrailingPunct, t("profile.last_name_leading_trailing_punctuation"))
      .min(2, t("profile.last_name_min"))
      .max(30, t("profile.last_name_max")),

    company_name: yup
      .string()
      .trim()
      .when([], {
        is: () => role === "Vendor",
        then: (schema) =>
          schema
            .required(t("profile.business_name_required"))
            .matches(notBlankRegex, t("profile.first_name_spaces"))
            .matches(noHTMLRegex, t("validations.no_html_tags"))
            .matches(noCodeRegex, t("validations.no_code_content"))
            .matches(
              noConsecutiveSpecialChars,
              t("profile.business_name_consecutive_special_chars")
            )
            .matches(
              noLeadingTrailingPunct,
              t("profile.business_name_leading_trailing_punctuation")
            )
            .min(2, t("profile.business_name_min"))
            .max(50, t("profile.business_name_max")),
        otherwise: (schema) => schema.notRequired(),
      }),

    business_type: yup
      .string()
      .when([], {
        is: () => role === "Vendor",
        then: (schema) =>
          schema
            .required(t("profile.business_type_required"))
            .min(1, t("profile.business_type_min"))
            .max(50, t("profile.business_type_max")),
        otherwise: (schema) => schema.notRequired(),
      }),

    email: yup
      .string()
      .trim()
      .required(t("validations.email_required"))
      .matches(notBlankRegex, t("validations.email_invalid_format"))
      .matches(noHTMLRegex, t("validations.no_html_tags"))
      .matches(noCodeRegex, t("validations.no_code_content"))
      .email(t("validations.email_invalid"))
      .max(50, t("validations.email_max", { max: 50 })),

    mobile_number: yup
      .string()
      .trim()
      .required(t("validations.mobile_number_required"))
      .matches(notBlankRegex, t("validations.mobile_number_invalid_format"))
      .matches(/^\d+$/, t("validations.mobile_number_only_digits"))
      .min(8, t("validations.mobile_number_min_max", { min: 8, max: 15 }))
      .max(15, t("validations.mobile_number_min_max", { min: 8, max: 15 }))
      .test("not-all-zeros", t("validations.mobile_number_all_zeros"), (value) => {
        if (!value) return false;
        return !/^0+$/.test(value);
      }),

    country_code: yup
      .string()
      .trim()
      .required(t("validations.country_code_required"))
      .max(5, t("validations.country_code_max")),

    alternate_number: yup
      .string()
      .nullable()
      .transform((value, originalValue) => (originalValue === "" ? null : value))
      .trim()
      .test("not-blank", t("validations.alternate_number_invalid_format"), (value) => !value || notBlankRegex.test(value))
      .test("is-valid", t("validations.alternate_number_invalid"), (value) => !value || /^[0-9]{8,15}$/.test(value))
      .test("not-all-zeros", t("validations.mobile_number_all_zeros"), (value) => !value || !/^0+$/.test(value)),

    alternate_country_code: yup
      .string()
      .nullable()
      .trim()
      .max(5, t("validations.alternate_country_code_max"))
      .transform((value, originalValue) => (originalValue === "" ? null : value))
      .when("alternate_number", {
        is: (val) => val && val.trim() !== "",
        then: (schema) => schema.required(t("validations.alternate_country_code_required")),
        otherwise: (schema) => schema.nullable(),
      }),

    permanent_address: yup
      .string()
      .when([], {
        is: () => role === "Vendor",
        then: (schema) =>
          schema
            .required(t("validations.address_required"))
            .min(2, t("validations.address_min_max", { min: 2, max: 100 }))
            .max(100, t("validations.address_min_max", { min: 2, max: 100 })),
        otherwise: (schema) => schema.notRequired(),
      })
      .trim()
      .matches(notBlankRegex, t("profile.first_name_spaces"))
      .matches(noHTMLRegex, t("validations.no_html_tags"))
      .matches(noCodeRegex, t("validations.no_code_content"))
      .matches(noConsecutiveSpecialChars, t("validations.no_consecutive_special_chars", { field: t("address") }))
      .matches(noLeadingTrailingPunct, t("validations.no_leading_trailing_punctuation", { field: t("address") })),

    address_proof_image: yup.mixed().when('address_proof_visible', {
      is: (role) => role === 'Vendor',
      then: () => fileValidation('address_proof_image', isImageRemoved, t).required(t("profile.address_proof_required")),
      otherwise: (schema) => schema.notRequired(),
    }),
    commercial_reg_image: yup.mixed().when('commercial_reg_visible', {
      is: (role) => role === 'Vendor',
      then: () => fileValidation('commercial_reg_image', isImageRemoved, t).required(t("profile.commercial_reg_image_required")),
      otherwise: (schema) => schema.notRequired(),
    }),
    vat_certificate_image: yup.mixed().when('vat_certificate_visible', {
      is: (role) => role === 'Vendor',
      then: () => fileValidation('vat_certificate_image', isImageRemoved, t).required(t("profile.vat_certificate_image_required")),
      otherwise: (schema) => schema.notRequired(),
    }),
    passbook_image: yup.mixed().when('passbook_visible', {
      is: (role) => role === 'Vendor',
      then: () => fileValidation('passbook_image', isImageRemoved, t).required(t("profile.passbook_image_required")),
      otherwise: (schema) => schema.notRequired(),
    }),
    residence_image: yup.mixed().when('residence_visible', {
      is: (role) => role === 'Vendor',
      then: () => fileValidation('residence_image', isImageRemoved, t).required(t("profile.residence_image_required")),
      otherwise: (schema) => schema.notRequired(),
    }),

    profile_image: fileValidation('profile_image', isImageRemoved, t),


    commercial_reg_number: yup.string().trim().when([], {
      is: () => role === "Vendor",
      then: (schema) =>
        schema
          .required(t("profile.commercial_reg_number_required"))
          .matches(notBlankRegex, t("profile.first_name_spaces"))
          .matches(noHTMLRegex, t("validations.no_html_tags"))
          .matches(noCodeRegex, t("validations.no_code_content"))
          .matches(noConsecutiveSpecialChars, t("profile.commercial_reg_number_consecutive_special_chars"))
          .matches(noLeadingTrailingPunct, t("profile.commercial_reg_number_leading_trailing_punctuation"))
          .min(2, t("profile.commercial_reg_number_min"))
          .max(30, t("profile.commercial_reg_number_max")),
      otherwise: (schema) => schema.notRequired(),
    }),

    vat_certificate_number: yup.string().trim().when([], {
      is: () => role === "Vendor",
      then: (schema) =>
        schema
          .required(t("profile.vat_certificate_number_required"))
          .matches(notBlankRegex, t("profile.first_name_spaces"))
          .matches(noHTMLRegex, t("validations.no_html_tags"))
          .matches(noCodeRegex, t("validations.no_code_content"))
          .matches(noConsecutiveSpecialChars, t("profile.vat_certificate_number_consecutive_special_chars"))
          .matches(noLeadingTrailingPunct, t("profile.vat_certificate_number_leading_trailing_punctuation"))
          .min(2, t("profile.vat_certificate_number_min"))
          .max(30, t("profile.vat_certificate_number_max")),
      otherwise: (schema) => schema.notRequired(),
    }),

    account_number: yup.string().trim().when([], {
      is: () => role === "Vendor",
      then: (schema) =>
        schema
          .required(t("profile.account_number_required"))
          .matches(notBlankRegex, t("profile.first_name_spaces"))
          .matches(noHTMLRegex, t("validations.no_html_tags"))
          .matches(noCodeRegex, t("validations.no_code_content"))
          .matches(noConsecutiveSpecialChars, t("profile.account_number_consecutive_special_chars"))
          .matches(noLeadingTrailingPunct, t("profile.account_number_leading_trailing_punctuation"))
          .min(8, t("profile.account_number_min"))
          .max(15, t("profile.account_number_max")),
      otherwise: (schema) => schema.notRequired(),
    }),

    bank_swift_code: yup.string().trim().when([], {
      is: () => role === "Vendor",
      then: (schema) =>
        schema
          .string()
          .required(t("profile.bank_iban_required"))
          .length(24, t("profile.bank_iban_length"))
          .matches(/^[A-Z0-9]{24}$/, t("profile.bank_iban_invalid_format")),
      otherwise: (schema) => schema.notRequired(),
    }),

    account_holder_first_name: yup
      .string()
      .nullable()
      .transform((value) => (value === "" ? null : value))
      .trim()
      .when([], {
        is: () => role === "Vendor",
        then: (schema) =>
          schema
            .required(t("profile.account_holder_first_name_required"))
            .matches(notBlankRegex, t("profile.first_name_spaces"))
            .matches(noHTMLRegex, t("validations.no_html_tags"))
            .matches(noCodeRegex, t("validations.no_code_content"))
            .matches(noConsecutiveSpecialChars, t("profile.account_holder_first_name_consecutive_special_chars"))
            .matches(noLeadingTrailingPunct, t("profile.account_holder_first_name_leading_trailing_punctuation"))
            .min(2, t("profile.account_holder_first_name_min"))
            .max(30, t("profile.account_holder_first_name_max")),
        otherwise: (schema) => schema.nullable(),
      }),

    other_documents: yup
      .mixed()
      .nullable()
      .test("fileType", t("validations.file_type_media"), (value) => {
        if (!value) return true;
        const files = Array.isArray(value) ? value : [value];
        return files.every((file) => {
          if (typeof file === "string") return true;

          if (file?.file && typeof file.file === "string") return true;

          if (file instanceof File) {
            return file.type.startsWith("image/") || file.type === "application/pdf";
          }

          return false;
        });
      })
      .test("fileCount", t("validations.file_count_max", { count: 5 }), (value) => {
        if (!value) return true;
        const files = Array.isArray(value) ? value : [value];
        return files.length <= 5;
      }),

    residence_id: yup
      .string()
      .trim()
      .required(t("validations.residence_id_required"))
      .matches(notBlankRegex, t("validations.residence_id_invalid_format"))
      .matches(noHTMLRegex, t("validations.no_html_tags"))
      .matches(noCodeRegex, t("validations.no_code_content"))
      .matches(noConsecutiveSpecialChars, t("validations.no_consecutive_special_chars", { field: t("residence_id_or_iqama_id") }))
      .matches(noLeadingTrailingPunct, t("validations.no_leading_trailing_punctuation", { field: t("residence_id_or_iqama_id") }))
      .min(2, t("validations.residence_id_min_max"))
      .max(30, t("validations.residence_id_min_max")),

    other_documents_detail: yup
      .string()
      .nullable()
      .test("not-only-spaces", t("validations.no_spaces_only"), (value) => {
        if (value === null || value === undefined) return true;
        if (value === "") return true;
        return value.trim().length > 0;
      })
      .matches(noHTMLRegex, t("validations.no_html_tags"))
      .matches(noCodeRegex, t("validations.no_code_content"))
      .matches(noConsecutiveSpecialChars, t("validations.no_consecutive_special_chars", { field: t("other_documents") }))
      .matches(noLeadingTrailingPunct, t("validations.no_leading_trailing_punctuation", { field: t("other_documents") }))
      .max(100, t("validations.other_docs_detail_max", { max: 100 })),

    country_id: yup
      .string()
      .trim()
      .when([], {
        is: () => role !== "Vendor",
        then: (schema) => schema.required(t("validations.country_required")),
        otherwise: (schema) => schema.notRequired(),
      }),

    city_id: yup
      .string()
      .trim()
      .when([], {
        is: () => role !== "Vendor",
        then: (schema) => schema.required(t("validations.city_required")),
        otherwise: (schema) => schema.notRequired(),
      }),

    office_id: yup
      .string()
      .trim()
      .when([], {
        is: () => role !== "Vendor",
        then: (schema) => schema.required(t("validations.office_required")),
        otherwise: (schema) => schema.notRequired(),
      }),
  });

const isImageRemoved = {
  address_proof_image: false,
  commercial_reg_image: false,
  vat_certificate_image: false,
  passbook_image: false,
  residence_image: false,
  profile_image: false,
};


export function Profile() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation("internal-user");
  const { t: ct } = useTranslation("common");

  const initialSegment =
    searchParams.get("tab") === "change_password"
      ? "change_password"
      : "profile";

  const [segment, setSegment] = useState(initialSegment);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [documentUrl, setDocumentUrl] = useState('');
  const [isShowLoader, setIsShowLoader] = useState(true);

  const { user, loading } = useSelector((state) => state.user);

  const activeTab =
    location.state?.active_tab || searchParams.get("tab");

  const shownRef = useRef(false);
  useEffect(() => {
    if (activeTab === "change_password" && !shownRef.current) {
      shownRef.current = true;

      setSegment("change_password");

      const swalWithBootstrapButtons = Swal.fire({
        title: t("profile.update_password_prompt_title"),
        text: t("profile.update_password_prompt_text"),
        icon: "warning",
        showDenyButton: true, 
        showCancelButton: false,
        confirmButtonText: t("update"),
        denyButtonText: t("profile.skip"),
        reverseButtons: true,
        customClass: {
          confirmButton: "btn btn-success ms-2", 
          denyButton: "btn btn-secondary"       
        },
        buttonsStyling: false
      }).then((result) => {
        if (result.isConfirmed) {
          // redirect to change password page
        } else if (result.isDenied) {
          localStorage.setItem("is_change_password_denied", "true");
          navigate("/dashboard/home");
        }
      });

      
    }
  }, [activeTab, t, navigate]);

  useEffect(() => {
    if (user?.id) {
      reset({
        id: user?.id,
        first_name: user?.first_name,
        last_name: user?.last_name,
        email: user?.email,
        mobile_number: user?.mobile_number,
        residence_id: user?.residence_id,
        profile_image: user?.profile_image,
        business_type: user?.business_type_,
        company_name: user?.company_name,
        role: user?.roles[0],
        country_code: user?.country_code,
        residence_image: user?.residence_image,
        alternate_number: user?.alternate_number,
        alternate_country_code: user?.alternate_country_code,
        country_id: user?.office?.country_id,
        city_id: user?.office?.city_id,
        office_id: user?.office?.id,
        sales_work_type: user?.sales_work_type,
        permanent_address: user?.kyc_details?.permanent_address,
        address_proof_image: user?.kyc_details?.address_proof_image,
        commercial_reg_number: user?.kyc_details?.commercial_reg_number,
        commercial_reg_image: user?.kyc_details?.commercial_reg_image,
        vat_certificate_number: user?.kyc_details?.vat_certificate_number,
        vat_certificate_image: user?.kyc_details?.vat_certificate_image,
        account_number: user?.bank_account?.account_number,
        passbook_image: user?.bank_account?.passbook_image,
        bank_swift_code: user?.bank_account?.bank_swift_code,
        account_holder_first_name: user?.bank_account?.first_name,
        other_documents_detail: user?.other_documents_detail,
        other_documents: user?.other_docs,
      });
      setIsShowLoader(false);
    }
    dispatch(fetchCity(user?.office?.country_id));
    dispatch(fetchOffice(user?.office?.city_id));
  }, [user, segment, dispatch]);

  useEffect(() => {
    dispatch(fetchCountryMobileCode());
    dispatch(getBusinessType('vendor'));
    dispatch(fetchCountry());
  }, [dispatch]);

  const { mobile_country_code } = useSelector((state) => state.internalUser);
  const { cities, countries, offices, } = useSelector((state) => state.internalUser);
  const { business_types } = useSelector((state) => state.customer);

  const { register, handleSubmit, control, setValue, watch, formState: { errors }, reset } = useForm({
    resolver: yupResolver(schema(user?.roles[0], isImageRemoved, t)),
    defaultValues: {
      id: '',
      first_name: '',
      last_name: '',
      email: '',
      mobile_number: '',
      residence_id: '',
      profile_image: '',
      business_type: user?.business_type_,
      company_name: '',
      role: user?.roles[0],
      country_code: '',
      residence_image: '',
      alternate_number: '',
      alternate_country_code: '',
      country_id: null,
      city_id: null,
      office_id: null,
      sales_work_type: '',
      permanent_address: '',
      address_proof_image: '',
      commercial_reg_number: '',
      commercial_reg_image: '',
      vat_certificate_number: '',
      vat_certificate_image: '',
      account_number: '',
      passbook_image: '',
      bank_swift_code: 'SA',
      account_holder_first_name: '',
      other_documents_detail: '',
      other_documents: [],
    }
  });

  const { register: registerPassword, handleSubmit: handleChangePasswordSubmit, formState: { errors: passwordErrors }, reset: resetPassword, } = useForm({
    resolver: yupResolver(passwordSchema(t)),
    defaultValues: {
      id: user?.id,
      old_password: "",
      password: "",
      password_confirmation: "",
    },
  });

  const onSubmit = async (data) => {
    try {
      const result = await dispatch(updateProfile(data)).unwrap();
      if (result.status) {
        Toaster.success(result.message || t("user_updated_successfully", { type: "User" }));
        dispatch(resetStatus());
        dispatch(getUserProfile());

      } else {
        Toaster.error(result.message || t("something_went_wrong"));
        dispatch(resetStatus());
      }
    } catch (err) {
      Toaster.error(err?.message || t("profile.failed_to_update_user"));
      dispatch(resetStatus());
    }
  };

  const onChangePassword = async (data) => {
    try {
      const result = await dispatch(changePassword(data)).unwrap();
      if (result.status) {
        dispatch(resetStatus());
        resetPassword();
        const logoutPromise = Promise.resolve();

        logoutPromise.then(() => {
          Toaster.success(t("profile.password_changed_success"));
          localStorage.clear();
          navigate('/sign-in');
          setTimeout(() => {
            dispatch(clearUser());
          }, 200);
        }).catch((error) => {
          console.error("Logout failed:", error);
          Toaster.error(t("profile.logout_failed"));
        });
      } else {
        Toaster.error(result.message || t("something_went_wrong"));
        dispatch(resetStatus());
      }
    } catch (err) {
      Toaster.error(err?.message || t("profile.failed_to_change_password"));
      dispatch(resetStatus());
    }
  };
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const toggleOldPassword = () => {
    setShowOldPassword(!showOldPassword);
  }
  const togglePassword = () => {
    setShowPassword(!showPassword);
  }
  const toggleConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  }

  const removeFile = (field) => {
    setValue(field, null, { shouldValidate: false });
    isImageRemoved[field] = true;
  };

  const idProof = watch("residence_image");
  const profileImage = watch('profile_image');

  const other_documents = watch('other_documents') || [];
  const passbook_image = watch('passbook_image');
  const address_proof_image = watch('address_proof_image');
  const vat_certificate_image = watch('vat_certificate_image');
  const commercial_reg_image = watch('commercial_reg_image');

  const handleFileChange = (e, field) => {
    const files = Array.from(e.target.files);

    if (field === "other_documents") {
      const existingDocs = watch("other_documents") || [];

      setValue("other_documents", [...existingDocs, ...files], { shouldValidate: true });
    } else {
      setValue(field, files[0], { shouldValidate: true });
    }
    isImageRemoved[field] = false;
  };

  const removeDocument = (index, documentId = null) => {
    const updated = [...other_documents];
    const removedDoc = updated.splice(index, 1);
    setValue("other_documents", updated);

    if (documentId) {
      Swal.fire({
        title: t("profile.are_you_sure"),
        text: t("profile.revert_warning"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: t("profile.yes_delete")
      }).then((result) => {
        if (result.isConfirmed) {
          dispatch(deleteOtherDocument(documentId)).unwrap().then(() => {
            Toaster.success(t("profile.other_document_deleted_successfully"));
          }).catch((error) => {
            Toaster.error(t("profile.failed_to_delete_document"));
            setValue("other_documents", [...updated, removedDoc[0]]);
          });
        } else {
          setValue("other_documents", [...updated, removedDoc[0]]);
        }
      });
    }
  };

  const onError = (err) => {
    console.log(err);
  }

  return (
    <>
      <div className="mt-5 mb-8 flex flex-col gap-8 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {(loading || isShowLoader) && <ShowLoader />}
        <ScrollToTop />
        <form onSubmit={segment === 'change_password' ? handleChangePasswordSubmit(onChangePassword) : handleSubmit(onSubmit, onError)} className="space-y-2">
          <div className="sticky top-0 z-30 p-5 bg-blue-gray-f5">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 md:gap-2">
              <div className="flex items-center gap-2">
                <Typography variant="h5" color="blue-gray" className="font-extrabold text-gray-800">
                  {t("profile.edit_details")}
                </Typography>
              </div>
              <div className="flex items-center gap-2 justify-end">
                <Button
                  type="button"
                  variant="outlined"
                  onClick={() => navigate(`/dashboard/profile`)}
                  className="rounded-lg"
                >
                  {t("cancel")}
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="flex items-center gap-2 border-gray-300 capitalize font-bold bg-black text-white">
                  {loading
                    ? segment === 'profile'
                      ? t("profile.updating")
                      : t("profile.changing")
                    : segment === 'profile'
                      ? t("update")
                      : t("profile.change")}

                </Button>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Link to={`/dashboard/profile`} className="flex items-center text-blue-600 hover:text-blue-800">
                <ArrowLeftIcon className="h-4 w-4 mr-1" />
                {t("back")}
              </Link>
            </div>
          </div>
          <div className="mt-1 mb-6 border border-blue-gray-100 rounded-lg p-5 bg-white px-4 md:px-6">
            <div className="mb-10 flex items-center justify-between flex-wrap gap-6">
              <div className="flex items-center gap-6">
                <Avatar
                  src={user.profile_image ? user.profile_image : '/img/photo-gallery.png'}
                  alt={t("profile_alt")}
                  size="xl"
                  variant="rounded"
                  className={`rounded-lg shadow-lg shadow-blue-gray-500/40 ${user?.profile_image ? 'cursor-pointer' : ''}`}
                  onClick={() => {
                    if (user?.profile_image) {
                      setIsModalOpen(true); setDocumentUrl(user?.profile_image)
                    }
                  }}
                />
                <div className="space-y-1.5">
                  <Typography variant="h5" color="blue-gray" className="mb-1">
                    {t("profile.first_last_name", { first_name: user.first_name, last_name: user.last_name || '' })}
                  </Typography>
                  <Typography
                    variant="small"
                    className="font-normal text-blue-gray-600"
                  >
                    ({t("profile.user_role", { role: user?.roles[0] })})
                  </Typography>
                  <Typography
                    variant="small"
                    className="font-normal text-blue-gray-600"
                  >
                    {t("profile.user_email", { email: user.email })}
                  </Typography>
                </div>
              </div>
              <div className="w-96">
                <Tabs value={segment}>
                  <TabsHeader>
                    <Tab value="profile" onClick={() => setSegment('profile')}>
                      <HomeIcon className="-mt-1 mr-2 inline-block h-5 w-5" />
                      {t("profile_alt")}
                    </Tab>
                    <Tab value="change_password" onClick={() => setSegment('change_password')}>
                      <Cog6ToothIcon className="-mt-1 mr-2 inline-block h-5 w-5" />
                      {t("profile.settings")}
                    </Tab>
                  </TabsHeader>
                </Tabs>
              </div>
            </div>

            {segment === 'change_password' &&
              <div>
                <Typography variant="h4" color="blue-gray" className="mb-5 font-medium">
                  {t("profile.change_password")}
                </Typography>
                <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
                  <div className="space-y-6">
                    <div className="mb-1 flex flex-col gap-6">
                      <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                        {t("profile.old_password")}  <span className="text-red-500">*</span>
                      </Typography>
                      <div className="password-wrapper">
                        <Input
                          type={showOldPassword ? "text" : "password"}
                          size="lg"
                          placeholder={t("profile.password_placeholder")}
                          className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                          labelProps={{
                            className: "before:content-none after:content-none",
                          }}
                          {...registerPassword("old_password")}
                          error={Boolean(passwordErrors.old_password)}
                        />
                        {showOldPassword ? (
                          <EyeIcon className="eye-icon" onClick={toggleOldPassword} />
                        ) : (
                            <EyeSlashIcon className="eye-icon" onClick={toggleOldPassword}></EyeSlashIcon>
                        )}
                      </div>
                      {passwordErrors.old_password && (
                        <p className="text-red-500 text-sm">{passwordErrors.old_password?.message}</p>
                      )}

                      <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                        {t("profile.new_password")}  <span className="text-red-500">*</span>
                      </Typography>
                      <div className="password-wrapper">
                        <Input
                          type={showPassword ? "text" : "password"}
                          size="lg"
                          placeholder={t("profile.password_placeholder")}
                          className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                          labelProps={{
                            className: "before:content-none after:content-none",
                          }}
                          {...registerPassword("password")}
                          error={Boolean(passwordErrors.password)}
                        />
                        {showPassword ? (
                          <EyeIcon className="eye-icon" onClick={togglePassword} />
                        ) : (
                          <EyeSlashIcon className="eye-icon" onClick={togglePassword}></EyeSlashIcon>
                        )}
                      </div>
                      {passwordErrors.password && (
                        <p className="text-red-500 text-sm">{passwordErrors.password?.message}</p>
                      )}

                      <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                        {t("profile.confirm_password")} <span className="text-red-500">*</span>
                      </Typography>
                      <div className="password-wrapper">
                        <Input
                          type={showConfirmPassword ? "text" : "password"}
                          size="lg"
                          placeholder={t("profile.password_placeholder")}
                          className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                          labelProps={{
                            className: "before:content-none after:content-none",
                          }}
                          {...registerPassword("password_confirmation")}
                          error={Boolean(passwordErrors.password_confirmation)}
                        />
                        {showConfirmPassword ? (
                          <EyeIcon className="eye-icon" onClick={toggleConfirmPassword} />
                        ) : (
                          <EyeSlashIcon className="eye-icon" onClick={toggleConfirmPassword}></EyeSlashIcon>
                        )}

                      </div>
                      {passwordErrors.password_confirmation && (
                        <p className="text-red-500 text-sm">{passwordErrors.password_confirmation?.message}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>}
            {segment === 'profile' &&
              <div className="space-y-10">
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                  <div className="flex flex-col gap-1">
                    <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                      {user?.roles[0] === 'Vendor' ? t("first_name") : t("full_name")} <span className="text-red-500">*</span>
                    </Typography>
                    <Input
                      type="text"
                      size="lg"
                      placeholder={t("profile.enter_first_name")}
                      className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                      labelProps={{
                        className: "before:content-none after:content-none",
                      }}
                      {...register("first_name")}
                      error={Boolean(errors.first_name)}
                    />
                    {errors.first_name && (
                      <p className="text-red-500 text-sm">{errors.first_name?.message}</p>
                    )}
                  </div>
                  {user?.roles[0] === 'Vendor' && <div className="flex flex-col gap-1">
                    <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                      {t("last_name")}
                    </Typography>
                    <Input
                      type="text"
                      size="lg"
                      placeholder={t("profile.enter_last_name")}
                      className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                      labelProps={{
                        className: "before:content-none after:content-none",
                      }}
                      {...register("last_name")}
                      error={Boolean(errors.last_name)}
                    />
                    {errors.last_name && (
                      <p className="text-red-500 text-sm">{errors.last_name?.message}</p>
                    )}
                  </div>}
                  <div className="xl:col-span-1 flex flex-col gap-1">
                    <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                      {t("phone_number")} <span className="text-red-500">*</span>{t("with_whatsapp_access")}
                    </Typography>
                    <div className="grid grid-cols-1 md:grid-cols-6 gap-0">
                      <div className="md:col-span-3">
                        <Select
                          type="text"
                          size="lg"
                          className="!border-t-blue-gray-200 focus:!border-t-gray-900"
                          labelProps={{
                            className: "before:content-none after:content-none",
                          }}
                          placeholder={t("select")}
                          onChange={(code) => {
                            setValue('country_code', code?.value, { shouldValidate: true });
                          }}
                          value={mobile_country_code?.find(opt => opt.value === watch('country_code')) || null}
                          options={mobile_country_code}
                          error={Boolean(errors.country_code)}
                          isClearable
                          styles={{
                            control: (provided, state) => ({
                              ...provided,
                              minHeight: '44px',
                              height: '44px',
                            }),
                          }}
                          components={{
                            Option: CountryOption,
                            SingleValue: CountrySingleValue
                          }}
                        />
                      </div>
                      <div className="md:col-span-3">
                        <Input
                          type="number"
                          size="lg"
                          min='0'
                          placeholder={t("enter_phone_number")}
                          className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                          labelProps={{
                            className: "before:content-none after:content-none",
                          }}
                          {...register("mobile_number")}
                          error={Boolean(errors.mobile_number)}
                        />
                      </div>
                    </div>
                    {(errors.mobile_number || errors.country_code) && (
                      <p className="text-red-500 text-sm">{errors.mobile_number?.message ? errors.mobile_number?.message : errors.country_code.message}</p>
                    )}
                  </div>
                  <div className="xl:col-span-1 flex flex-col gap-1">
                    <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                      {t("alternate_number")}
                    </Typography>
                    <div className="grid grid-cols-1 md:grid-cols-6 gap-0">
                      <div className="md:col-span-3">
                        <Select
                          type="text"
                          placeholder={t("select")}
                          onChange={(code) => {
                            setValue('alternate_country_code', code?.value, { shouldValidate: true });
                          }}
                          value={mobile_country_code?.find(opt => opt.value === watch('alternate_country_code')) || null}
                          options={mobile_country_code}
                          error={Boolean(errors.alternate_country_code)}
                          isClearable
                          styles={{
                            control: (provided, state) => ({
                              ...provided,
                              minHeight: '44px',
                              height: '44px',
                            }),
                          }}
                        />
                      </div>
                      <div className="md:col-span-3">
                        <Input
                          type="number"
                          size="lg"
                          min='0'
                          placeholder={t("enter_alternate_number")}
                          className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                          labelProps={{
                            className: "before:content-none after:content-none",
                          }}
                          {...register("alternate_number")}
                          error={Boolean(errors.alternate_number)}
                          onChange={(e) => {
                            let value = e.target.value;
                            if (!value || value.trim() === "") {
                              setValue("alternate_number", '', { shouldValidate: true });
                              setValue("alternate_country_code", null, { shouldValidate: true });
                            }
                          }}
                        />
                      </div>
                    </div>
                    {(errors.alternate_number || errors.alternate_country_code) && (
                      <p className="text-red-500 text-sm">{errors.alternate_number?.message ? errors.alternate_number?.message : errors.alternate_country_code.message}</p>
                    )}
                  </div>
                  <div className="flex flex-col gap-1">
                    <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                      {t("profile_photo")} <span className="text-red-500">*</span>
                    </Typography>
                    <div className="flex items-center gap-2">
                      {!profileImage ?
                        <div className="w-16 h-16 flex items-center justify-center rounded-full border-2 border-dashed border-gray-300 ml-3 mr-5">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="w-6 h-6 text-gray-400"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={1.5}
                              d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zM4 20c0-2.67 5.33-4 8-4s8 1.33 8 4"
                            />
                          </svg>
                        </div>
                        :
                        <FilePreview
                          file={profileImage}
                          onRemove={() => removeFile('profile_image')}
                          isView={false}
                          containerClass="relative w-16 h-16 rounded-full border-2 border-gray-300 overflow-hidden flex items-center justify-center bg-gray-50"
                        />
                      }
                      <input
                        id="profilePhoto"
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files[0] || null;
                          setValue('profile_image', file, { shouldValidate: true });
                        }}
                      /><p className="text-xs text-gray-400 mt-1">{t("jpg_png_upto_2mb")}</p>
                      <label
                        htmlFor="profilePhoto"
                        className="h-11 w-48 cursor-pointer px-4 py-2 bg-green-100 text-green-800 border border-green-300 rounded-md hover:bg-green-200 text-sm font-medium flex items-center justify-center gap-2"
                      >
                        <DocumentPlusIcon className="h-4 w-4 cursor-pointer text-green-800"></DocumentPlusIcon> {t("choose_file")}
                      </label>
                    </div>
                    {errors.profile_image && (
                      <p className="text-red-500 text-sm mt-2">{errors.profile_image?.message}</p>
                    )}
                  </div>
                  <div className="flex flex-col gap-1">
                    <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                      {t("residence_id_or_iqama_id")} <span className="text-red-500">*</span>
                    </Typography>
                    <div className="flex items-center gap-2">
                      <Input
                        type="text"
                        size="lg"
                        placeholder={t("enter_residence_id_or_iqama_id")}
                        className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                        labelProps={{
                          className: "before:content-none after:content-none",
                        }}
                        {...register("residence_id")}
                        error={Boolean(errors.residence_id)}
                      />

                      <input
                        id="residenceNumberFile"
                        type="file"
                        accept="image/*,application/pdf"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files[0] || null;
                          setValue('residence_image', file, { shouldValidate: true });
                        }}
                      />
                      <label
                        htmlFor="residenceNumberFile"
                        className="h-11 w-48 cursor-pointer px-4 py-2 bg-green-100 text-green-800 border border-green-300 rounded-md hover:bg-green-200 text-sm font-medium flex items-center justify-center gap-2"
                      >
                        <DocumentPlusIcon className="h-4 w-4 cursor-pointer text-green-800"></DocumentPlusIcon> {t("choose_file")}
                      </label>
                    </div>
                    <p className="text-xs text-gray-400">{t("jpg_png_upto_2mb")}</p>
                    {(errors.residence_id?.message || errors.residence_image?.message) && (
                      <p className="text-red-500 text-sm mt-2">
                        {errors.residence_id?.message || errors.residence_image?.message}
                      </p>
                    )}
                    {idProof &&
                      <div className="mt-3">
                        <FilePreview
                          file={idProof}
                          onRemove={() => removeFile('residence_image')}
                          isView={false}
                          containerClass="relative w-24 h-24 rounded-md overflow-hidden border border-gray-300"
                        />
                      </div>
                    }
                  </div>
                  {user?.roles[0] === 'Vendor' && <>
                    <div className="flex flex-col gap-1">
                      <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                        {t("profile.business_name")}  <span className="text-red-500">*</span>
                      </Typography>
                      <Input
                        type="text"
                        size="lg"
                        placeholder={t("profile.enter_business_name")}
                        className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                        labelProps={{
                          className: "before:content-none after:content-none",
                        }}
                        {...register("company_name")}
                        error={Boolean(errors.company_name)}
                      />
                      {errors.company_name && (
                        <p className="text-red-500 text-sm">{errors.company_name?.message}</p>
                      )}
                    </div>
                    <div className="flex flex-col gap-1">
                      <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                        {t("profile.vendor_type")} <span className="text-red-500">*</span>
                      </Typography>
                      <Select
                        placeholder={t("profile.select_vendor_type")}
                        onChange={(business_type) => {
                          setValue('business_type', business_type?.value, { shouldValidate: true });
                        }}
                        error={Boolean(errors.business_type)}
                        options={business_types}
                        value={business_types?.find(opt => opt.value == watch('business_type')) || null}
                        isClearable
                      />
                      {errors.business_type && (
                        <p className="text-red-500 text-sm">{errors.business_type?.message}</p>
                      )}
                    </div>
                  </>}
                </div>
                {user.roles[0] !== 'Vendor' && <div className="mt-1 mb-6">
                  <div className="mb-5 pb-2 border-b border-blue-gray-200">
                    <Typography variant="h4" color="blue-gray">
                      {t("assign_office")}
                    </Typography>
                  </div>
                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                    <div className="md:col-span-2 w-full flex items-center gap-3">
                      <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                        {t("is_this_person_an_office_incharge")}
                      </Typography>

                      <label className="flex items-center gap-1 cursor-pointer">
                        <input
                          type="checkbox"
                          value="office"
                          {...register("sales_work_type")}
                          checked={watch('sales_work_type') == 'office'}
                          className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <span className="text-gray-700 text-sm">{t("yes")}</span>
                      </label>
                    </div>
                    <div className="flex flex-col gap-1">
                      <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                        {t("country")} <span className="text-red-500">*</span>
                      </Typography>
                      <Select
                        placeholder={t("select_country")}
                        onChange={(country) => {
                          setValue('country_id', country?.value, { shouldValidate: true });
                          dispatch(fetchCity(country?.value));
                        }}
                        error={Boolean(errors.country_id)}
                        options={countries}
                        value={countries?.find(opt => opt.value === watch('country_id')) || null}
                        isClearable
                      />
                      {errors.country_id && (
                        <p className="text-red-500 text-sm">{errors.country_id?.message}</p>
                      )}
                    </div>
                    <div className="flex flex-col gap-1">
                      <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                        {t("city_territory")} <span className="text-red-500">*</span>
                      </Typography>
                      <Select
                        placeholder={t("select_city")}
                        onChange={(city) => {
                          setValue('city_id', city?.value, { shouldValidate: true });
                          dispatch(fetchOffice(city?.value))
                        }}
                        error={Boolean(errors.city_id)}
                        options={cities}
                        value={cities?.find(opt => opt.value === watch('city_id')) || null}
                        isClearable
                      />
                      {errors.city_id && (
                        <p className="text-red-500 text-sm">{errors.city_id?.message}</p>
                      )}
                    </div>
                    <div className="flex flex-col gap-1">
                      <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                        {t("office")} <span className="text-red-500">*</span>
                      </Typography>
                      <Select
                        placeholder={t("select_office")}
                        onChange={(office) => {
                          setValue('office_id', office?.value, { shouldValidate: true });
                        }}
                        error={Boolean(errors.office_id)}
                        options={offices}
                        value={offices?.find(opt => opt.value === watch('office_id')) || null}
                        isClearable
                      />
                      {errors.office_id && (
                        <p className="text-red-500 text-sm">{errors.office_id?.message}</p>
                      )}
                    </div>
                  </div>
                </div>}
                {user.roles[0] === 'Vendor' && <div className="mt-1 mb-6">
                  <div className="mb-5 pb-2 border-b border-blue-gray-200">
                    <Typography variant="h4" color="blue-gray">
                      {t("profile.kyc_details")}
                    </Typography>
                  </div>
                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                    <div className="md:col-span-2">
                      <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                        {t("profile.national_address")} <span className="text-red-500">*</span>
                      </Typography>
                      <div className="flex items-center gap-2">
                        <Input
                          type="text"
                          size="lg"
                          placeholder={t("profile.national_address_placeholder")}
                          className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                          labelProps={{
                            className: "before:content-none after:content-none",
                          }}
                          {...register("permanent_address")}
                          error={Boolean(errors.permanent_address)}
                        />
                        <input
                          id="addressFile"
                          type="file"
                          accept="image/*,application/pdf"
                          className="hidden"
                          {...register("address_proof_image")}
                          onChange={(e) => handleFileChange(e, 'address_proof_image')}
                        />
                        <label
                          htmlFor="addressFile"
                          className="h-11 w-48 cursor-pointer px-4 py-2 bg-green-100 text-green-800 border border-green-300 rounded-md hover:bg-green-200 text-sm font-medium flex items-center justify-center gap-2"
                        >
                          <DocumentPlusIcon className="h-4 w-4 cursor-pointer text-green-800"></DocumentPlusIcon> {t("choose_file")}
                        </label>
                      </div>
                      {(errors.permanent_address || errors.address_proof_image) && (
                        <p className="text-red-500 text-sm">{errors.permanent_address?.message ? errors.permanent_address?.message : errors.address_proof_image?.message}</p>
                      )}
                      <div className="mt-3">
                        {(address_proof_image) &&
                          <div className="relative w-24 h-24 rounded-md overflow-hidden border border-gray-300">

                            <FilePreview
                              file={address_proof_image}
                              onRemove={() => removeFile("address_proof_image")}
                              isView={false}
                              containerClass="relative w-24 h-24 rounded-md overflow-hidden border border-gray-300"
                            />
                          </div>}
                      </div>
                    </div>

                    <div className="flex flex-col gap-1">
                      <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                        {t("profile.commercial_reg_number")} <span className="text-red-500">*</span>
                      </Typography>
                      <div className="flex items-center gap-2">
                        <Input
                          type="text"
                          size="lg"
                          placeholder={t("profile.enter_certificate_number")}
                          className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                          labelProps={{
                            className: "before:content-none after:content-none",
                          }}
                          {...register("commercial_reg_number")}
                          error={Boolean(errors.commercial_reg_number)}
                        />
                        <input
                          id="commercialNumberFile"
                          type="file"
                          accept="image/*,application/pdf"
                          className="hidden"
                          onChange={(e) => handleFileChange(e, 'commercial_reg_image')}
                        />
                        <label
                          htmlFor="commercialNumberFile"
                          className="h-11 w-48 cursor-pointer px-4 py-2 bg-green-100 text-green-800 border border-green-300 rounded-md hover:bg-green-200 text-sm font-medium flex items-center justify-center gap-2"
                        >
                          <DocumentPlusIcon className="h-4 w-4 cursor-pointer text-green-800"></DocumentPlusIcon> {t("choose_file")}
                        </label>
                      </div>
                      {(errors.commercial_reg_number || errors.commercial_reg_image) && (
                        <p className="text-red-500 text-sm">{errors.commercial_reg_number?.message ? errors.commercial_reg_number?.message : errors.commercial_reg_image?.message}</p>
                      )}
                      <div className="mt-3">
                        {(commercial_reg_image) && (
                          <div className="relative w-24 h-24 rounded-md overflow-hidden border border-gray-300">

                            <FilePreview
                              file={commercial_reg_image}
                              onRemove={() => removeFile("commercial_reg_image")}
                              isView={false}
                              containerClass="relative w-24 h-24 rounded-md overflow-hidden border border-gray-300"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col gap-1">
                      <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                        {t("profile.vat_certificate_number")} <span className="text-red-500">*</span>
                      </Typography>
                      <div className="flex items-center gap-2">
                        <Input
                          type="text"
                          size="lg"
                          placeholder={t("profile.enter_vat_number")}
                          className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                          labelProps={{
                            className: "before:content-none after:content-none",
                          }}
                          {...register("vat_certificate_number")}
                          error={Boolean(errors.vat_certificate_number)}
                        />
                        <input
                          id="vatNumberFile"
                          type="file"
                          accept="image/*,application/pdf"
                          className="hidden"
                          onChange={(e) => handleFileChange(e, 'vat_certificate_image')}
                        />
                        <label
                          htmlFor="vatNumberFile"
                          className="h-11 w-48 cursor-pointer px-4 py-2 bg-green-100 text-green-800 border border-green-300 rounded-md hover:bg-green-200 text-sm font-medium flex items-center justify-center gap-2"
                        >
                          <DocumentPlusIcon className="h-4 w-4 cursor-pointer text-green-800"></DocumentPlusIcon> {t("choose_file")}
                        </label>
                      </div>
                      {(errors.vat_certificate_number || errors.vat_certificate_image) && (
                        <p className="text-red-500 text-sm">{errors.vat_certificate_number?.message ? errors.vat_certificate_number?.message : errors.vat_certificate_image?.message}</p>
                      )}
                      <div className="mt-3">
                        {(vat_certificate_image) && (
                          <div className="relative w-24 h-24 rounded-md overflow-hidden border border-gray-300">

                            <FilePreview
                              file={vat_certificate_image}
                              onRemove={() => removeFile("vat_certificate_image")}
                              isView={false}
                              containerClass="relative w-24 h-24 rounded-md overflow-hidden border border-gray-300"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col gap-1">
                      <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                        {t("profile.account_number")} <span className="text-red-500">*</span>
                      </Typography>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          size="lg"
                          placeholder={t("profile.enter_account_number")}
                          className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                          labelProps={{
                            className: "before:content-none after:content-none",
                          }}
                          {...register("account_number")}
                          error={Boolean(errors.account_number)}
                        />
                        <input
                          id="accountNumberFile"
                          type="file"
                          accept="image/*,application/pdf"
                          className="hidden"
                          onChange={(e) => handleFileChange(e, 'passbook_image')}
                        />
                        <label
                          htmlFor="accountNumberFile"
                          className="h-11 w-48 cursor-pointer px-4 py-2 bg-green-100 text-green-800 border border-green-300 rounded-md hover:bg-green-200 text-sm font-medium flex items-center justify-center gap-2"
                        >
                          <DocumentPlusIcon className="h-4 w-4 cursor-pointer text-green-800"></DocumentPlusIcon> {t("choose_file")}
                        </label>
                      </div>
                      {(errors.account_number || errors.passbook_image) && (
                        <p className="text-red-500 text-sm">{errors.account_number?.message ? errors.account_number?.message : errors.passbook_image?.message}</p>
                      )}
                      <div className="mt-3">
                        {(passbook_image) && <>
                          <div className="relative w-24 h-24 rounded-md overflow-hidden border border-gray-300">

                            <FilePreview
                              file={passbook_image}
                              onRemove={() => removeFile("passbook_image")}
                              isView={false}
                              containerClass="relative w-24 h-24 rounded-md overflow-hidden border border-gray-300"
                            />
                          </div>
                        </>}
                      </div>
                    </div>
                    <div className="flex flex-col gap-1">
                      <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                        {t("profile.bank_iban")} <span className="text-red-500">*</span>
                      </Typography>
                      <Input
                        type="text"
                        size="lg"
                        placeholder={t("profile.enter_bank_iban")}
                        className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                        labelProps={{
                          className: "before:content-none after:content-none",
                        }}
                        {...register("bank_swift_code", {
                          onChange: (e) => {
                            e.target.value = e.target.value.toUpperCase();
                          },
                        })}
                        error={Boolean(errors.bank_swift_code)}
                      />
                      {errors.bank_swift_code && (
                        <p className="text-red-500 text-sm">{errors.bank_swift_code?.message}</p>
                      )}
                    </div>
                    <div className="flex flex-col gap-1">
                      <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                        {t("profile.beneficiary_account_holder_full_name")}
                      </Typography>
                      <Input
                        type="text"
                        size="lg"
                        placeholder={t("enter_full_name")}
                        className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                        labelProps={{
                          className: "before:content-none after:content-none",
                        }}
                        {...register("account_holder_first_name")}
                        error={Boolean(errors.account_holder_first_name)}
                      />
                      {errors.account_holder_first_name && (
                        <p className="text-red-500 text-sm">{errors.account_holder_first_name?.message}</p>
                      )}
                    </div>
                    <div className="md:col-span-2">
                      <Typography variant="small" color="blue-gray" className="mb-2 font-medium">
                        {t("other_documents")}
                      </Typography>
                      <div className="flex items-center gap-2">
                        <Input
                          type="text"
                          size="lg"
                          placeholder={t("profile.choose_other_document")}
                          className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                          labelProps={{
                            className: "before:content-none after:content-none",
                          }}
                          {...register('other_documents_detail')}
                        />
                        <input
                          id="otherDocumentFile"
                          type="file"
                          accept="image/*,application/pdf"
                          multiple
                          className="hidden"
                          onChange={(e) => handleFileChange(e, "other_documents")}
                        />
                        <label
                          htmlFor="otherDocumentFile"
                          className="h-11 w-48 cursor-pointer px-4 py-2 bg-green-100 text-green-800 border border-green-300 rounded-md hover:bg-green-200 text-sm font-medium flex items-center justify-center gap-2"
                        >
                          <DocumentPlusIcon className="h-4 w-4 text-green-800" />
                          {t("choose_file")}
                        </label>
                      </div>
                      {errors.other_documents_detail && (
                        <p className="text-red-500 text-sm">{errors.other_documents_detail?.message}</p>
                      )}
                      {errors?.other_documents && (
                        <p className="text-red-500 text-sm mt-1">
                          {errors.other_documents.message || t("validations.invalid_file_type")}
                        </p>
                      )}
                    </div>
                    {other_documents.length > 0 && (
                      <div className="md:col-span-2">
                        <div className="mt-3 flex flex-wrap gap-6">
                          {other_documents?.map((doc, index) => {
                            const isExisting = !!doc?.id;
                            const file = isExisting ? doc.file : doc;
                            const docName = isExisting ? doc.document_name : doc.name || doc?.file?.name || "Document";

                            return (
                              <FilePreview
                                key={index}
                                file={file}
                                onRemove={() => removeDocument(index, isExisting ? doc.id : "")}
                                isView={false}
                                containerClass="relative w-24 h-24 rounded-md overflow-hidden border border-gray-300"
                                className="object-cover w-full h-full"
                              />
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>}
              </div>
            }
          </div>
        </form>
      </div>
      {isModalOpen && <DocumentViewModal documentUrl={documentUrl} setIsDocumentViewModalOpen={setIsModalOpen}></DocumentViewModal>}
    </>
  );
}

export default Profile;
